import { Badge, Card } from "@/components/ui";
import type { ProactiveLogItem } from "@/types";
import { compactTime } from "@/utils/format";
import { cn } from "@/lib/utils";
import { kindLabel, outcomeDetail, outcomeDisplay } from "@/utils/engagement";
import { PROACTIVE_TABLE_COLUMNS } from "./proactiveTable";

/** Colour only the outcomes that carry a clear verdict. */
const TONE_CLASS = {
  good: "border-success-line bg-success-surface text-success",
  bad: "border-destructive-line bg-destructive-surface text-destructive",
  warn: "border-accent-line bg-accent text-accent-foreground",
  neutral: "",
} as const;

export function ProactiveLogRow({
  item,
  selected,
  onSelect,
}: {
  item: ProactiveLogItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const outcome = outcomeDisplay(item.outcome);
  const detail = outcomeDetail(item);
  const derived = item.signals.opened_at?.source === "derived";

  return (
    <Card className={cn("overflow-hidden", selected && "border-primary bg-primary/8")}>
      <button
        type="button"
        onClick={onSelect}
        className={`${PROACTIVE_TABLE_COLUMNS} min-h-16 w-full cursor-pointer border-0 bg-transparent px-4.5 py-3.5 text-left text-inherit hover:bg-accent-hover`}
      >
        <span className="text-muted-foreground">{compactTime(item.created_at)}</span>
        <Badge size="compact">{kindLabel(item.kind)}</Badge>
        <span className="min-w-0 truncate">{item.recipient_email || "-"}</span>
        <span className="min-w-0 truncate text-muted-foreground">
          {item.session.display_name || item.session.session_id || "-"}
        </span>
        <span className="min-w-0 truncate text-muted-foreground">
          {(item.content || "").trim() || "(본문 없음)"}
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-1.5" title={outcome.hint}>
          <Badge size="compact" className={TONE_CLASS[outcome.tone]}>
            {outcome.label}
          </Badge>
          {detail && <span className="text-xs text-muted-foreground">{detail}</span>}
          {/* Only on values that were inferred, never on observed ones. */}
          {derived && item.outcome === "opened" && (
            <Badge size="compact" title="직접 관측이 아니라 푸시 확인·세션 읽음에서 추정한 값입니다.">
              추정
            </Badge>
          )}
        </span>
        <Badge size="compact">{item._environment}</Badge>
      </button>
    </Card>
  );
}
